import { FC, useCallback, useMemo } from 'react'
import cn from 'classnames'
import themes from 'styles/base/themes.styl'
import { CreditCardPanePreset } from 'components/features/creditCard/creditCardPanes/CreditCardPanePreset'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { CCLockCardAction, CCUnlockCardAction, ResetCCLockCardAction } from 'components/features/creditCard/creditCardStore/actions'
import { creditCardErrorsSelector, currentCardSelector, lockCardRequestSelector, unlockCardRequestSelector } from 'components/features/creditCard/creditCardStore/selectors'
import { CardStatus } from 'components/features/creditCard/creditCardPage/CardStatus/CardStatus'
import FloatingPaneNotification from 'components/features/pfm/FloatingPaneNotification/FloatingPaneNotification'
import { useHover } from '@react-aria/interactions'
import { ToggleCardLockActionButton } from 'components/features/pfm/Button/ActionButtons'
import { KillAllPanesButton } from 'components/features/pfm/Button/FloatingPaneButtons'

const LockCardPane: FC<{}> = () => {
  const { hoverProps, isHovered } = useHover({})
  const lockCardRequest = useStoreSelector(lockCardRequestSelector)
  const unlockCardRequest = useStoreSelector(unlockCardRequestSelector)
  const isLoading = lockCardRequest.isLoading || unlockCardRequest.isLoading
  const errors = useStoreSelector(creditCardErrorsSelector)
  const card = useStoreSelector(currentCardSelector)
  const cardIsLocked = card?.status === 'suspended'
  const hoverIcon = cardIsLocked ? 'unlock' : 'lock'

  const dispatch = useStoreDispatch()
  const handleResetCCRequests = useCallback(() => {
    dispatch(new ResetCCLockCardAction())
  }, [dispatch])

  const handleToggleCardLock = useCallback(() => {
    if (!card) {
      return
    }
    if (cardIsLocked) {
      return dispatch(new CCUnlockCardAction({ uid: card.uid }))
    } else {
      return dispatch(new CCLockCardAction({ uid: card.uid }))
    }
  }, [cardIsLocked, card, dispatch])

  const errorChild = useMemo(() => errors?.length > 0 && (
    <FloatingPaneNotification
      messages={ errors }
      onDismiss={ handleResetCCRequests }
    />
  ), [errors, handleResetCCRequests])
  const standardIcon = hoverIcon === 'unlock' ? 'lock' : 'unlock'
  return (
    <CreditCardPanePreset
      title='Lock/Unlock'
      isLoading={ isLoading }
      notification={ errorChild }
      tertiaryButton={ <KillAllPanesButton text='Close' style='textOnly' /> }
    >
      <figure>
        <CardStatus
          status={ card?.status }
          isLoading={ isLoading }
          iconSlug={ isHovered ? hoverIcon : standardIcon }
          onClick={ handleToggleCardLock }
          hoverProps={ hoverProps }
        />
      </figure>
      <section>
        <h2>
          Your card is currently
          <strong className={ cn(themes.textOnly, themes.green, cardIsLocked && themes.danger) }>
            { cardIsLocked ? ' Locked' : ' Unlocked' }
          </strong>.
        </h2>
        <h4>
          When locked, no one will be able to use this card, including
          you. You can unlock it at any time.{ ' ' }
        </h4>
        <p>
          Card Lock will prevent new card purchases, ATM transactions, recurring
          payments and refunds. However, some payments such as ACH Lockbox,
          pending transactions, dispute adjustments, and accounts fees (late fees,
          annual fees) may continue even with a Card Lock in place.
        </p>
      </section>
      <ToggleCardLockActionButton fullWidth />
      <small>
        For more information on payments and fees, please see the Consumer
        Credit Card Agreement.
      </small>
    </CreditCardPanePreset>
  )
}

export default LockCardPane
